import type { AppConfiguration } from "@/api/app-configuration.api";
import { isAdmin } from "@/services/auth.service";
import type { User } from "@/types/auth";
import {
  groupIdentify,
  identify,
  Identify,
  initAll,
  logEvent,
  reset,
  setGroup,
  setUserId,
  track,
} from "@amplitude/unified";

export { logEvent, setUserId };

interface Claims {
  impersonating?: boolean;
  [key: string]: unknown;
}

/**
 * Initializes Amplitude with user identity and group identity.
 * Should be called once authentication data is available.
 *
 * @returns A cleanup function that calls `reset()`, or `undefined` if no API key is set.
 */
export function initAmplitude(
  user: User,
  appConfiguration: AppConfiguration,
  claims: Claims,
): (() => void) | undefined {
  const apiKey = import.meta.env.VITE_AMPLITUDE_API_KEY;
  if (!apiKey) return;

  // Identify the user
  const userIdentity = new Identify()
    .set("Email", user.email)
    .set("Role", isAdmin(user) ? "Salesleader" : "Sales")
    .set("Created", user.created);

  setUserId(user.id);

  initAll(apiKey, {
    serverZone: "EU",
    analytics: {
      userId: user.id,
      identify: userIdentity,
      optOut: !!claims.impersonating,
      autocapture: {
        pageViews: false,
        networkTracking: true,
        frustrationInteractions: true,
      },
    },
    sessionReplay: {
      identify: userIdentity,
      sampleRate: import.meta.env.DEV ? 1 : 0.7,
      ...(import.meta.env.DEV ? { debugMode: true } : {}),
    },
  });

  identify(userIdentity);

  // Set organization group identity
  const organization = user.organization;
  const solution = organization.currentSolution;

  setGroup("workspace_id", organization.lassoOrganizationId);

  const groupIdentity = new Identify()
    .set("Name", organization.name)
    .set(
      "Plan",
      !solution || !solution.isActive
        ? "expired"
        : appConfiguration.subscription.trial
          ? "trial"
          : "paid",
    );

  if (solution) {
    groupIdentity.set("Solution", solution.versionUniqueName);
  }

  if (organization.lassoId) {
    groupIdentity.set("Cvr", organization.lassoId.substring(6));
  }

  groupIdentity.set("Created", organization.created);

  groupIdentify(
    "workspace_id",
    organization.lassoOrganizationId,
    groupIdentity,
  );

  return () => {
    reset();
  };
}

/**
 * Tracks a page view event in Amplitude.
 */
export function trackPageView(pagePath: string, pageUrl: string): void {
  if (import.meta.env.VITE_AMPLITUDE_LOG_EVENTS === "true") {
    console.debug("[amplitude.service.ts] Tracking page view:", {
      pagePath,
      pageUrl,
    });
  }

  const apiKey = import.meta.env.VITE_AMPLITUDE_API_KEY;
  if (!apiKey) return;

  track("Page Viewed", {
    page_path: pagePath,
    page_url: pageUrl,
  });
}

/**
 * Tracks a custom event in Amplitude.
 */
export function trackEvent(
  eventName: string,
  userId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  properties?: Record<string, any>,
): void {
  if (import.meta.env.VITE_AMPLITUDE_LOG_EVENTS === "true") {
    console.debug("[amplitude.service.ts] Tracking event:", {
      eventName,
      userId,
      properties,
    });
  }

  const apiKey = import.meta.env.VITE_AMPLITUDE_API_KEY;
  if (!apiKey) return;

  logEvent(eventName, properties, { user_id: userId });
}

/**
 * Tracks the outcome of starting a delivery (campaign run), either right after
 * campaign creation or as a manual delivery from the campaign page.
 */
export function trackDeliveryScheduled(
  userId: string,
  {
    source,
    campaignId,
    campaignType,
    options,
    requestedLeads,
    error,
  }: {
    source: "Campaign creation" | "Manual delivery";
    campaignId: number;
    campaignType: string;
    options: {
      amount: number;
      deliverContacts: boolean;
      deliverContactsRequirePhone: boolean;
      deliverLiveNumber: boolean;
      completionEmail: string | null;
    };
    /** The amount the backend accepted, which may be adjusted down from `options.amount`. */
    requestedLeads?: number;
    error?: unknown;
  },
): void {
  trackEvent(
    error === undefined ? "Delivery scheduled" : "Delivery scheduling failed",
    userId,
    {
      source,
      campaignId,
      campaignType,
      amount: options.amount,
      requestedLeads,
      deliverContacts: options.deliverContacts,
      deliverContactsRequirePhone: options.deliverContactsRequirePhone,
      deliverLiveNumber: options.deliverLiveNumber,
      hasCompletionEmail: !!options.completionEmail,
      ...(error !== undefined
        ? {
            error:
              error instanceof Error
                ? `${error.name}: ${error.message}`
                : String(error),
          }
        : {}),
    },
  );
}
